/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, Grid, Icon } from 'bold-ui'
import { Form, FormDebouncedValueSpy, SwitchField } from 'components/form'
import { TipoRegistroSelectField } from 'components/form/field/select/TipoRegistroSelectField/TipoRegistroSelectField'
import { PopperButton, PopperControls } from 'components/popper'
import { useFlags } from 'config/useFlagsContext'
import { FormState } from 'final-form'
import { DisponibilidadeDadosClinicosHistorico } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useFilterCounter } from 'hooks/useFilterCounter'
import { Fragment, useCallback } from 'react'
import { ExibirPorHistoricoAtendimentoFilterEnum } from 'types/enums'
import { metaPath } from 'util/metaPath'

import {
  FILTROS_AVANCADOS_DEFAULT_VALUE,
  HistoricoAtendimentoAdvancedFilterFormModel,
  HistoricoAtendimentoFilterFormModel,
} from '../../model/model-historico'
import HistoricoAtendimentoActiveFilters from './HistoricoAtendimentoActiveFilters'
import HistoricoAtendimentoFilterPopper from './HistoricoAtendimentoFilterPopper'
import { HistoricoAtendimentoSortDropdown } from './HistoricoAtendimentoSortDropDown'

const path = metaPath<HistoricoAtendimentoFilterFormModel>()

export interface HistoricoAtendimentoFilterProps {
  filtros: HistoricoAtendimentoFilterFormModel
  disponibilidadeDadosClinicos: DisponibilidadeDadosClinicosHistorico
  onChangeFiltros(values: HistoricoAtendimentoFilterFormModel): void
}

export default function HistoricoAtendimentoFilter(props: HistoricoAtendimentoFilterProps) {
  const { filtros, disponibilidadeDadosClinicos, onChangeFiltros } = props
  const { analytics } = useFirebase()
  const { RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED } = useFlags()
  const counterFiltrosAvancadosAplicados = useFilterCounter(filtros.filtrosAvancados, {
    exibirRegistrosInativos: RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED,
  })

  const handleChangeFiltrosAvancados = useCallback(
    (values: HistoricoAtendimentoAdvancedFilterFormModel) => {
      let newFilter = { ...filtros, filtrosAvancados: values }

      newFilter.pageParams.page = 0

      if (
        filtros.periodoExibicao === ExibirPorHistoricoAtendimentoFilterEnum.PERSONALIZADO &&
        !values.periodo?.startDate &&
        !values.periodo?.endDate
      ) {
        newFilter.periodoExibicao = ExibirPorHistoricoAtendimentoFilterEnum.TODOS_REGISTROS
      } else if (values.periodo?.startDate || values.periodo?.endDate) {
        newFilter.periodoExibicao = ExibirPorHistoricoAtendimentoFilterEnum.PERSONALIZADO
      }

      onChangeFiltros(newFilter)
    },
    [filtros, onChangeFiltros]
  )

  const handleChangeFiltros = useCallback(
    (formState: FormState<HistoricoAtendimentoFilterFormModel>) => {
      let newFilter = { ...filtros, ...formState.values }

      newFilter.pageParams.page = 0

      if (newFilter.periodoExibicao !== ExibirPorHistoricoAtendimentoFilterEnum.PERSONALIZADO) {
        newFilter.filtrosAvancados.periodo = undefined
      }

      onChangeFiltros(newFilter)
    },
    [filtros, onChangeFiltros]
  )

  const onClearFiltrosAvancados = () => {
    onChangeFiltros({
      ...filtros,
      periodoExibicao: ExibirPorHistoricoAtendimentoFilterEnum.TODOS_REGISTROS,
      filtrosAvancados: FILTROS_AVANCADOS_DEFAULT_VALUE,
    })
  }

  const renderPopper = ({ close }: PopperControls) => {
    analytics.logEvent('open_filtros_avancados_historico')
    return (
      <HistoricoAtendimentoFilterPopper
        initialValues={filtros.filtrosAvancados}
        clear={onClearFiltrosAvancados}
        close={close}
        onSubmit={handleChangeFiltrosAvancados}
        disponibilidadeDadosClinicos={disponibilidadeDadosClinicos}
      />
    )
  }

  const renderFiltroAdministrativo = () => {
    return (
      <Fragment>
        <FormDebouncedValueSpy onChange={handleChangeFiltros} wait={400} />
        <Cell size={4}>
          <TipoRegistroSelectField name={path.tiposRegistro} label='Tipo de registro' multiple />
        </Cell>
        <Cell
          size={3}
          style={css`
            align-items: flex-end;
            display: inline-flex;
            justify-content: flex-start;
          `}
        >
          <SwitchField name={path.somenteMeusRegistros} label='Ver somente os meus registros' />
        </Cell>
        <Cell
          size={3}
          style={css`
            align-items: flex-end;
            display: inline-flex;
            justify-content: flex-end;
          `}
        >
          <HistoricoAtendimentoSortDropdown name={path.periodoExibicao} />
        </Cell>
      </Fragment>
    )
  }

  return (
    <Grid>
      <Form<HistoricoAtendimentoFilterFormModel>
        render={renderFiltroAdministrativo}
        onSubmit={onChangeFiltros}
        initialValues={
          {
            periodoExibicao: filtros.periodoExibicao,
            somenteMeusRegistros: filtros.somenteMeusRegistros,
            tiposRegistro: filtros.tiposRegistro,
          } as any
        }
      />
      <Cell
        size={2}
        style={css`
          align-items: flex-end;
          display: inline-flex;
          justify-content: flex-end;
        `}
      >
        <PopperButton kind='primary' size='small' skin='default' placement='bottom-end' renderPopper={renderPopper}>
          <Icon icon='filterOutline' />
          {`Outros filtros ${counterFiltrosAvancadosAplicados > 0 ? ` (${counterFiltrosAvancadosAplicados}) ` : ''}`}
        </PopperButton>
      </Cell>
      <Cell size={12}>
        <HistoricoAtendimentoActiveFilters filters={filtros.filtrosAvancados} />
      </Cell>
    </Grid>
  )
}
